// src/components/PreviewGate.js
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import TermsOfUse from './TermsOfUse';
import PrivacyPolicy from './PrivacyPolicy';
import './PreviewGate.css';

// Define the allowed states with unique 'value' for display and a 'dataCode' for actual data fetching
const DISPLAY_STATES_CONFIG = [
  { value: '', label: 'Select State', dataCode: '' }, // Default option
  { value: 'AL_DISPLAY', label: 'Alabama', dataCode: 'AL' },
  { value: 'AK_DISPLAY', label: 'Alaska', dataCode: 'AK' },
  { value: 'AZ_DISPLAY', label: 'Arizona', dataCode: 'AZ' },
  { value: 'AR_DISPLAY', label: 'Arkansas', dataCode: 'AR' },
  { value: 'CA_DISPLAY', label: 'California', dataCode: 'CA' },
  { value: 'CO_DISPLAY', label: 'Colorado', dataCode: 'CO' },
  { value: 'CT_DISPLAY', label: 'Connecticut', dataCode: 'CT' },
  { value: 'DE_DISPLAY', label: 'Delaware', dataCode: 'DE' },
  { value: 'DC_DISPLAY', label: 'District of Columbia', dataCode: 'DC' },
  { value: 'FL_DISPLAY', label: 'Florida', dataCode: 'FL' },
  { value: 'GA_DISPLAY', label: 'Georgia', dataCode: 'GA' },
  { value: 'HI_DISPLAY', label: 'Hawaii', dataCode: 'HI' },
  { value: 'ID_DISPLAY', label: 'Idaho', dataCode: 'ID' },
  { value: 'IL_DISPLAY', label: 'Illinois', dataCode: 'IL' },
  { value: 'IN_DISPLAY', label: 'Indiana', dataCode: 'IN' },
  { value: 'IA_DISPLAY', label: 'Iowa', dataCode: 'IA' },
  { value: 'KS_DISPLAY', label: 'Kansas', dataCode: 'KS' },
  { value: 'KY_DISPLAY', label: 'Kentucky', dataCode: 'KY' },
  { value: 'LA_DISPLAY', label: 'Louisiana', dataCode: 'LA' },
  { value: 'ME_DISPLAY', label: 'Maine', dataCode: 'ME' },
  { value: 'MD_DISPLAY', label: 'Maryland', dataCode: 'MD' },
  { value: 'MA_DISPLAY', label: 'Massachusetts', dataCode: 'MA' },
  { value: 'MI_DISPLAY', label: 'Michigan', dataCode: 'MI' },
  { value: 'MN_DISPLAY', label: 'Minnesota', dataCode: 'MN' },
  { value: 'MS_DISPLAY', label: 'Mississippi', dataCode: 'MS' },
  { value: 'MO_DISPLAY', label: 'Missouri', dataCode: 'MO' },
  { value: 'MT_DISPLAY', label: 'Montana', dataCode: 'MT' },
  { value: 'NE_DISPLAY', label: 'Nebraska', dataCode: 'NE' },
  { value: 'NV_DISPLAY', label: 'Nevada', dataCode: 'NV' },
  { value: 'NH_DISPLAY', label: 'New Hampshire', dataCode: 'NH' },
  { value: 'NJ_DISPLAY', label: 'New Jersey', dataCode: 'NJ' },
  { value: 'NM_DISPLAY', label: 'New Mexico', dataCode: 'NM' },
  { value: 'NY_DISPLAY', label: 'New York', dataCode: 'NY' },
  { value: 'NC_DISPLAY', label: 'North Carolina', dataCode: 'NC' },
  { value: 'ND_DISPLAY', label: 'North Dakota', dataCode: 'ND' },
  { value: 'OH_DISPLAY', label: 'Ohio', dataCode: 'OH' },
  { value: 'OK_DISPLAY', label: 'Oklahoma', dataCode: 'OK' },
  { value: 'OR_DISPLAY', label: 'Oregon', dataCode: 'OR' },
  { value: 'PA_DISPLAY', label: 'Pennsylvania', dataCode: 'PA' },
  { value: 'RI_DISPLAY', label: 'Rhode Island', dataCode: 'RI' },
  { value: 'SC_DISPLAY', label: 'South Carolina', dataCode: 'SC' },
  { value: 'SD_DISPLAY', label: 'South Dakota', dataCode: 'SD' },
  { value: 'TN_DISPLAY', label: 'Tennessee', dataCode: 'TN' },
  { value: 'TX_DISPLAY', label: 'Texas', dataCode: 'TX' },
  { value: 'UT_DISPLAY', label: 'Utah', dataCode: 'UT' },
  { value: 'VT_DISPLAY', label: 'Vermont', dataCode: 'VT' },
  { value: 'VA_DISPLAY', label: 'Virginia', dataCode: 'VA' },
  { value: 'WA_DISPLAY', label: 'Washington', dataCode: 'WA' },
  { value: 'WV_DISPLAY', label: 'West Virginia', dataCode: 'WV' },
  { value: 'WI_DISPLAY', label: 'Wisconsin', dataCode: 'WI' },
  { value: 'WY_DISPLAY', label: 'Wyoming', dataCode: 'WY' },
];

function PreviewGate({ isOpen, onGatePass }) {
  const [selectedDisplayValue, setSelectedDisplayValue] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [termsViewed, setTermsViewed] = useState(false);
  const [privacyViewed, setPrivacyViewed] = useState(false);
  const [showStateReminder, setShowStateReminder] = useState(false);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Prevent background scroll when policy modals are open
  useEffect(() => {
    if (showTermsModal || showPrivacyModal) {
      document.body.style.overflow = 'hidden';
    } else if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { 
      if (!isOpen && !showTermsModal && !showPrivacyModal) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, showTermsModal, showPrivacyModal]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Show reminder if state not selected
    if (!selectedDisplayValue) {
      setShowStateReminder(true);
      setError('Please select your state to continue.');
      // Auto-hide reminder after 5 seconds
      setTimeout(() => setShowStateReminder(false), 5000);
      return;
    }
    
    if (!acknowledged) {
      setError('You must acknowledge the disclaimer.');
      return;
    }

    // Find the selected state object to get its dataCode
    const selectedStateObject = DISPLAY_STATES_CONFIG.find(
      (state) => state.value === selectedDisplayValue
    );
    // Use the dataCode for checks and storage, or null if not found
    const actualDataCode = selectedStateObject ? selectedStateObject.dataCode : null;

    // Check if a valid display value was selected
    if (selectedDisplayValue) {
      setError('');
      setShowStateReminder(false);
      try {
        localStorage.setItem('hasPassedGate', 'true');
        // Store the actual data code, not the display value
        localStorage.setItem('selectedState', actualDataCode);
      } catch (storageError) {
        console.error("Error saving to localStorage:", storageError);
        setError("There was an issue saving your information. Please try again.");
        return;
      }
      onGatePass(actualDataCode); // Pass the actual data code to the parent
    }
  };

  const handleStateChange = (e) => {
    setSelectedDisplayValue(e.target.value);
    // Hide reminder when user selects a state
    if (e.target.value) {
      setShowStateReminder(false);
      setError('');
    }
  };

  const handleTermsClick = (e) => {
    e.preventDefault();
    setShowTermsModal(true);
  };

  const handlePrivacyClick = (e) => {
    e.preventDefault();
    setShowPrivacyModal(true);
  };

  const closeModal = (modalType) => {
    if (modalType === 'terms') {
      setShowTermsModal(false);
      setTermsViewed(true); // Mark as viewed when modal closes
    } else if (modalType === 'privacy') {
      setShowPrivacyModal(false);
      setPrivacyViewed(true); // Mark as viewed when modal closes
    }
  };

  if (!isOpen) {
    return null;
  }

  return ReactDOM.createPortal(
    <>
      <div className="preview-gate-container">
        <div className="preview-gate-card">
          <h2>Welcome</h2>
          <p>Please select your state and confirm the disclaimer to continue.</p>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="state">Your State:</label>
              <div className={`state-select-wrapper ${!selectedDisplayValue && showStateReminder ? 'state-select-reminder-active' : ''}`}>
                <select
                  id="state"
                  value={selectedDisplayValue}
                  onChange={handleStateChange}
                  required
                  className={!selectedDisplayValue && showStateReminder ? 'state-select-highlight' : ''}
                  aria-invalid={!selectedDisplayValue && showStateReminder}
                  aria-describedby={!selectedDisplayValue && showStateReminder ? 'state-reminder' : undefined}
                >
                  {DISPLAY_STATES_CONFIG.map((st) => (
                    <option key={st.value} value={st.value}>
                      {st.label}
                    </option>
                  ))}
                </select>
                {!selectedDisplayValue && showStateReminder && (
                  <div className="state-reminder-icon" role="alert" aria-live="polite">
                    <span className="reminder-icon">⚠️</span>
                  </div>
                )}
              </div>
              {!selectedDisplayValue && showStateReminder && (
                <div id="state-reminder" className="state-reminder-message" role="alert">
                  <span className="reminder-icon-small">📍</span>
                  <span>Please select your state to continue. This helps us provide you with the most relevant information.</span>
                </div>
              )}
            </div>

            <fieldset className="disclaimer-group">
              <legend>Legal Disclaimer</legend>
              <p>
                By clicking "Enter Site," I confirm that I am at least 21 years old and reside in a jurisdiction where sports betting is legal. I understand that all content on BoostYourBets.com is provided for informational purposes only and does not constitute financial or betting advice. I acknowledge that gambling involves risk and I may lose money. For help with problem gambling, I can call the National Problem Gambling Helpline at <a href="tel:1-800-GAMBLER" className="help-link">1-800-GAMBLER</a>.
              </p>
              
              <div className="policy-links-section">
                <p className="policy-links-intro">Please review the following policies:</p>
                <div className="policy-links">
                  <button 
                    type="button" 
                    className="policy-link-button"
                    onClick={handleTermsClick}
                    aria-label="Read Terms of Service"
                  >
                    {termsViewed ? '✓ ' : ''}Terms of Service
                  </button>
                  <button 
                    type="button" 
                    className="policy-link-button"
                    onClick={handlePrivacyClick}
                    aria-label="Read Privacy Policy"
                  >
                    {privacyViewed ? '✓ ' : ''}Privacy Policy
                  </button>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <input
                  type="checkbox"
                  id="acknowledge"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  required
                />
                <label htmlFor="acknowledge">
                  I confirm I have read and understand the <strong>Terms of Service</strong> and <strong>Privacy Policy</strong>, and agree to be bound by them.
                </label>
              </div>
            </fieldset>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={!acknowledged || !selectedDisplayValue}>
              Enter Site
            </button>
          </form>
        </div>
      </div>

      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="policy-modal-overlay" onClick={() => closeModal('terms')}>
          <div className="policy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="policy-modal-header">
              <h2>Terms of Service</h2>
              <button 
                className="policy-modal-close" 
                onClick={() => closeModal('terms')}
                aria-label="Close Terms of Service"
              >
                ×
              </button>
            </div>
            <div className="policy-modal-content terms-content">
              <TermsOfUse />
            </div>
            <div className="policy-modal-footer">
              <button 
                className="policy-modal-close-button"
                onClick={() => closeModal('terms')}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="policy-modal-overlay" onClick={() => closeModal('privacy')}>
          <div className="policy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="policy-modal-header">
              <h2>Privacy Policy</h2>
              <button 
                className="policy-modal-close" 
                onClick={() => closeModal('privacy')}
                aria-label="Close Privacy Policy"
              >
                ×
              </button>
            </div>
            <div className="policy-modal-content privacy-content">
              <PrivacyPolicy />
            </div>
            <div className="policy-modal-footer">
              <button 
                className="policy-modal-close-button"
                onClick={() => closeModal('privacy')}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}

export default PreviewGate;
